import React from "react";
import { createRoot, Root } from "react-dom/client";
import { TripPlayer } from "./player";
import type { TripPlayerHandle } from "./player";
import { exportToMp4 } from "./export-video";
import type { TripData, VideoConfig, ExportStatus } from "./types";

const MAPBOX_TOKEN =
    "pk.eyJ1Ijoic3RlcGhlbndpbnRlciIsImEiOiJjbW1peHBiNmYxNThvMnNzNWdvaHJzYWs2In0.RsBIE5FoydPcFgsW7qct3g";

class NeboTripVideo extends HTMLElement {
    private root: Root | null = null;
    private playerHandle: TripPlayerHandle | null = null;
    private _tripData: TripData | null = null;
    private _config: VideoConfig = {
        targetDurationSeconds: 30,
        cameraMode: "follow",
        terrainEnabled: true,
        trimStartSeconds: 0,
        trimEndSeconds: 0,
    };

    get tripData(): TripData | null {
        return this._tripData;
    }
    set tripData(value: TripData | null) {
        if (
            value?.title === this._tripData?.title &&
            value?.subtitle === this._tripData?.subtitle &&
            value?.dateLabel === this._tripData?.dateLabel &&
            value?.startTime === this._tripData?.startTime &&
            value?.endTime === this._tripData?.endTime &&
            value?.points.length === this._tripData?.points.length &&
            value?.photos.length === this._tripData?.photos.length
        )
            return;
        this._tripData = value;
        if (this.root) this.render();
    }

    get config(): VideoConfig {
        return this._config;
    }
    set config(value: VideoConfig) {
        if (JSON.stringify(value) === JSON.stringify(this._config)) return;
        this._config = value;
        if (this.root) this.render();
    }

    connectedCallback(): void {
        this.style.display = "block";
        this.style.position = "relative";

        const mountPoint = document.createElement("div");
        mountPoint.style.cssText = "width:100%;height:100%;";
        this.appendChild(mountPoint);

        this.root = createRoot(mountPoint);
        this.render();
    }

    disconnectedCallback(): void {
        this.root?.unmount();
        this.root = null;
    }

    seekTo(frame: number): void {
        this.playerHandle?.seekTo(frame);
    }
    play(): void {
        this.playerHandle?.play();
    }
    pause(): void {
        this.playerHandle?.pause();
    }

    async startExport(): Promise<void> {
        if (!this._tripData) {
            this.dispatchExportStatus({
                type: "error",
                progress: 0,
                errorMessage: "No trip data loaded",
            });
            return;
        }

        try {
            await exportToMp4(
                this._tripData,
                this._config,
                MAPBOX_TOKEN,
                (status) => this.dispatchExportStatus(status),
            );
        } catch (err) {
            this.dispatchExportStatus({
                type: "error",
                progress: 0,
                errorMessage: String(err),
            });
        }
    }

    private dispatchExportStatus(status: ExportStatus): void {
        this.dispatchEvent(
            new CustomEvent("export-status", { detail: status, bubbles: true }),
        );
    }

    private render(): void {
        if (!this.root || !this._tripData) return;
        console.log("Re-rendering trip player");

        this.root.render(
            React.createElement(TripPlayer, {
                tripData: this._tripData,
                config: this._config,
                mapboxToken: MAPBOX_TOKEN,
                onFrameChange: (frame: number) => {
                    this.dispatchEvent(
                        new CustomEvent("frame-change", {
                            detail: {
                                frame,
                                total: this.playerHandle?.getTotalFrames() ?? 0,
                            },
                            bubbles: true,
                        }),
                    );
                },
                ref: (handle: TripPlayerHandle | null) => {
                    this.playerHandle = handle;
                    if (handle) {
                        this.dispatchEvent(
                            new CustomEvent("frame-change", {
                                detail: {
                                    frame: 0,
                                    total: handle.getTotalFrames(),
                                },
                                bubbles: true,
                            }),
                        );
                    }
                },
            }),
        );
    }
}

export function registerTripVideo(): void {
    if (!customElements.get("nebo-trip-video")) {
        customElements.define("nebo-trip-video", NeboTripVideo);
    }
}
